import { TransactionRuleCondition } from "@common/types/transactionRule";

type ConditionValue = TransactionRuleCondition["value"];

function normalizeConditionValue(value: ConditionValue | null | undefined) {
    if (value == null) {
        return null;
    }
    if (Array.isArray(value)) {
        return [...value].sort((a, b) => String(a).localeCompare(String(b)));
    }
    if (typeof value === "object") {
        return Object.fromEntries(
            Object.entries(value).sort(([a], [b]) => a.localeCompare(b)),
        );
    }
    return value;
}

/**
 * Canonical string for a list of conditions, produced by sorting them by type
 * and normalizing array-valued entries and object key order. Makes set-comparison order- and
 * id-independent so duplicate detection and uniqueness checks can share the
 * same representation.
 */
export function canonicalizeConditions(
    conditions: Array<
        Pick<
            TransactionRuleCondition,
            "conditionType" | "value" | "transferDirection"
        >
    >,
): string {
    return JSON.stringify(
        [...conditions]
            .map((c) => ({
                conditionType: c.conditionType,
                transferDirection: c.transferDirection ?? null,
                value: normalizeConditionValue(c.value),
            }))
            .sort((a, b) => {
                const t = a.conditionType.localeCompare(b.conditionType);
                if (t !== 0) {
                    return t;
                }
                return JSON.stringify(a.value).localeCompare(
                    JSON.stringify(b.value),
                );
            }),
    );
}
